import {SurveyStatus} from 'crqlar-react-common/dist/models/survey/survey.model';
import {useTranslation} from 'react-i18next';

import {formatIdFilterNames, readSectorFilter, writeSectorFilter} from '../../../config/reservationTriggers';
import {
    formatGuestCategoryList,
    readGuestCategoryFilter,
    readSurveyFilter,
    writeSurveyTriggerConfig
} from '../../../config/surveyTriggers';
import NodeEditorLayout from '../NodeEditorLayout';
import {NodeEditorV2Props} from '../types';
import SectorMultiSelectField from './SectorMultiSelectField';
import SurveyTriggerFilters from './SurveyTriggerFilters';

/**
 * Which responses start a run: ≥1 survey, then optional guest category and sector. Filtering on the response
 * itself is a Continue if step; anonymous responses never start a run (no guest).
 */
const SurveySubmittedEditorV2 = ({
    nodeConfig,
    setNodeConfig,
    availableSurveys = [],
    availableSurveysLoading,
    readOnly
}: NodeEditorV2Props) => {
    const {t} = useTranslation();

    const surveyFilter = readSurveyFilter(nodeConfig);
    const sectorFilter = readSectorFilter(nodeConfig);
    const guestCategoryFilter = readGuestCategoryFilter(nodeConfig);
    const sectorName = formatIdFilterNames(sectorFilter, t);

    // A draft cannot be answered: warn until at least one picked survey is published.
    const selectedSurveys = surveyFilter.ids
        .map(id => availableSurveys.find(survey => survey.id === id))
        .filter((survey): survey is NonNullable<typeof survey> => !!survey);
    const surveysUnpublished = selectedSurveys.length > 0
        && selectedSurveys.every(survey => survey.status !== SurveyStatus.PUBLISHED);

    // The catalogue wins over the stored snapshot: a renamed survey should read by its current name.
    const surveyDisplayName = surveyFilter.ids
        .map(id => availableSurveys.find(survey => survey.id === id)?.name || surveyFilter.names[id])
        .filter(Boolean)
        .join(', ');

    // No "Any survey" on purpose: a flow is written for one kind of survey (see AUTOMATIONS_UI.md).
    const noSurvey = !surveyFilter.ids.length;

    const summary = () => {
        if (noSurvey) return t('Pick at least one survey, otherwise this trigger never fires.');
        if (surveysUnpublished) {
            return surveyFilter.ids.length > 1
                ? t('None of the chosen surveys is published yet, so this trigger cannot fire until one of them is.')
                : t('{{survey}} is not published yet, so this trigger cannot fire until it is.', {
                    survey: surveyDisplayName || t('This survey')
                });
        }

        const head = t('Runs once for every response to {{survey}}.', {survey: surveyDisplayName || t('a survey')});

        const guestClause = guestCategoryFilter.ids.length
            ? t('Only responses from these guests count: {{guests}}.', {
                guests: formatGuestCategoryList(guestCategoryFilter, t)
            })
            : '';

        const sectorClause = sectorName ? t('Only in {{sector}}.', {sector: sectorName}) : '';

        const tail = t('The response, its scores and the guest are available to the steps below.');

        return [head, guestClause, sectorClause, t('Anonymous responses are skipped.'), tail]
            .filter(Boolean)
            .join(' ');
    };

    return (
        <NodeEditorLayout state={noSurvey || surveysUnpublished ? 'wontRun' : 'configured'} summary={summary()}>
            <SurveyTriggerFilters
                nodeConfig={nodeConfig}
                setNodeConfig={setNodeConfig}
                availableSurveys={availableSurveys}
                availableSurveysLoading={availableSurveysLoading}
                excludeAnonymous
                readOnly={readOnly}
            />

            <SectorMultiSelectField
                label={t('Only in sector')}
                filter={sectorFilter}
                onChange={selection => setNodeConfig(prev => writeSurveyTriggerConfig(prev, writeSectorFilter(selection)))}
                disabled={readOnly}
            />
        </NodeEditorLayout>
    );
};

export default SurveySubmittedEditorV2;
